// =============================================================
// APPOINTMENT SERVICE
// Enterprise HTTP Client for OTP Generation & Appointment Booking
// =============================================================

import {
  SendOtpPayload,
  SendOtpResponse,
  VerifyAndBookPayload,
  BookAppointmentResponse,
} from '@/types/appointment'

/**
 * Configurable API endpoint base URL.
 * Will connect to backend when endpoints are provided in the next prompt.
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

// Mock adapter toggle: active when API_BASE_URL is empty
const USE_MOCK_ADAPTER = !API_BASE_URL

/**
 * Generic fetch wrapper with timeout, headers, and error parsing
 */
async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 12000)

  try {
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...options.headers,
      },
    })

    clearTimeout(timeoutId)

    const data = await res.json()
    if (!res.ok) {
      throw new Error(data.message || `Request failed with status ${res.status}`)
    }
    return data as T
  } catch (err: unknown) {
    clearTimeout(timeoutId)
    if (err instanceof Error) {
      if (err.name === 'AbortError') {
        throw new Error('Connection timed out. Please check your network and try again.')
      }
      throw err
    }
    throw new Error('An unexpected error occurred while communicating with the server.')
  }
}

// ─────────────────────────────────────────────────────────────
// Service Methods
// ─────────────────────────────────────────────────────────────

/**
 * Triggers OTP generation to the provided mobile number
 * POST /api/appointments/send-otp
 */
export async function sendOtp(payload: SendOtpPayload): Promise<SendOtpResponse> {
  if (!USE_MOCK_ADAPTER) {
    return apiRequest<SendOtpResponse>('/api/appointments/send-otp', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  // Enterprise Mock Simulation:
  // Simulates realistic network delay and returns standard enterprise envelope
  await new Promise((resolve) => setTimeout(resolve, 600))

  // Allow testing failure with mock number "0000000000"
  if (payload.mobileNumber === '0000000000') {
    throw new Error('Unable to send verification SMS to this number. Please verify and try again.')
  }

  return {
    success: true,
    message: `Verification code successfully sent to +91 ${payload.mobileNumber}`,
    sessionId: `sess_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    expiresInSeconds: 300,
  }
}

/**
 * Resends OTP to the patient
 * POST /api/appointments/resend-otp
 */
export async function resendOtp(payload: SendOtpPayload): Promise<SendOtpResponse> {
  if (!USE_MOCK_ADAPTER) {
    return apiRequest<SendOtpResponse>('/api/appointments/resend-otp', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  await new Promise((resolve) => setTimeout(resolve, 500))

  return {
    success: true,
    message: `A fresh verification code has been dispatched to +91 ${payload.mobileNumber}`,
    sessionId: `sess_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    expiresInSeconds: 300,
  }
}

/**
 * Verifies OTP and registers the booking
 * POST /api/appointments/verify-and-book
 */
export async function verifyAndBook(
  payload: VerifyAndBookPayload
): Promise<BookAppointmentResponse> {
  if (!USE_MOCK_ADAPTER) {
    return apiRequest<BookAppointmentResponse>('/api/appointments/verify-and-book', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  await new Promise((resolve) => setTimeout(resolve, 800))

  // Allow testing error flow with invalid OTP '0000' or '000000'
  if (payload.otp === '0000' || payload.otp === '000000') {
    throw new Error('Invalid verification code entered. Please check the SMS and re-enter, or click Resend OTP.')
  }

  // Generate dynamic appointment confirmation
  const appointmentId = `XYBB${Math.floor(100000 + Math.random() * 900000)}`

  return {
    success: true,
    message: 'Appointment confirmed successfully',
    appointment: {
      appointmentId,
      day: 'Monday',
      date: '24th October, 2026',
      validityTime: '7:00 AM to 11:00 AM',
      patientName: payload.name,
      mobileNumber: payload.mobileNumber,
      centerName: 'Nextgen Health Care Services (Authorized Center for Dr Lal PathLabs)',
      centerAddress: 'Manglabag Medical Road, Cuttack, Odisha, India',
      bookingTimestamp: new Date().toISOString(),
      instructions: [
        'Please bring a government-issued photo ID along with your Appointment ID.',
        '10-12 hours of overnight fasting is recommended if your tests include fasting glucose or lipid profiles.',
        'Please arrive between 7:00 AM and 11:00 AM on your appointed date for express sample collection.',
        'Digital pathology reports will be delivered via SMS/Email within 24 hours of sample intake.',
      ],
    },
  }
}

